import * as THREE from 'three';
import type { SketchSolid } from './types';

/**
 * Shared section-plane framing: the world pose (center + orientation + skin
 * size) used by BOTH the viewport marker and the gumball manipulator, so
 * the gizmo always sits exactly on the plane skin the user sees and picks.
 *
 * The center is the model's bounding-box center projected onto the plane
 * (the plane's nearest-to-origin point when the document is empty), the
 * orientation aligns local +Z with the plane normal, and the skin spans
 * 1.4x the model extent (20 units minimum) so the section always reads.
 */
export interface SectionPlaneFrame {
    center: THREE.Vector3;
    quaternion: THREE.Quaternion;
    size: number;
}

export function frameSectionPlane(
    plane: { normal: [number, number, number]; constant: number },
    solids: Record<string, SketchSolid>,
): SectionPlaneFrame {
    const n = new THREE.Vector3(...plane.normal).normalize();
    const box = new THREE.Box3();
    let found = false;
    Object.values(solids).forEach((solid) => {
        if (!solid.visible) return;
        solid.faces.forEach((face) => {
            face.outer.forEach((p) => box.expandByPoint(new THREE.Vector3(...p)));
            found = true;
        });
    });
    const modelCenter = found
        ? box.getCenter(new THREE.Vector3())
        : n.clone().multiplyScalar(-plane.constant);
    const signed = modelCenter.dot(n) + plane.constant;
    const center = modelCenter.addScaledVector(n, -signed);

    const modelSize = found
        ? Math.max(
            box.getSize(new THREE.Vector3()).x,
            box.getSize(new THREE.Vector3()).y,
            box.getSize(new THREE.Vector3()).z,
            1
        )
        : 0;
    const size = Math.max(modelSize * 1.4, 20);
    const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
    return { center, quaternion, size };
}
